import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  ChevronRight, 
  Phone, 
  Mail, 
  MapPin, 
  Star, 
  Check 
} from 'lucide-react';
import './Footer.css';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(true); // Set to true by default to avoid animation delays
  const footerRef = useRef(null);
  
  // Simplified approach with no overflow manipulation
  useEffect(() => {
    // No DOM manipulations that could affect scrolling
    return () => {
      // Clean cleanup with no side effects
    };
  }, []);
  
  return (
    <footer 
      ref={footerRef}
      className="circledin-footer visible" // Always visible, no animations
      style={{ touchAction: 'auto', pointerEvents: 'auto' }}
    >
      {/* Simplified background without animations */}
      <div className="circledin-footer__background">
        <div className="circledin-footer__blob circledin-footer__blob-1"></div>
        <div className="circledin-footer__blob circledin-footer__blob-2"></div>
        <div className="circledin-footer__blob circledin-footer__blob-3"></div>
      </div>
      
      {/* Stats Banner */}
      <div className="circledin-footer__stats">
        <div className="circledin-footer__stats-card">
          <h3 className="circledin-footer__stats-title">Customer Satisfaction</h3>
          <div className="circledin-footer__stars">
            {[...Array(5)].map((_, index) => (
              <Star key={index} size={18} className="circledin-footer__star filled" />
            ))}
          </div>
          <p className="circledin-footer__stats-value circledin-footer__text-gradient">90%</p>
          <p className="circledin-footer__stats-caption">of users recommend us</p>
        </div>
        
        <div className="circledin-footer__stats-card">
          <h3 className="circledin-footer__stats-title">Average Savings</h3>
          <p className="circledin-footer__stats-value circledin-footer__text-gradient">$480</p>
          <p className="circledin-footer__stats-caption">saved annually per user</p>
        </div>
        
        <div className="circledin-footer__stats-card">
          <h3 className="circledin-footer__stats-title">Plan Flexibility</h3>
          <div className="circledin-footer__features">
            <div className="circledin-footer__feature-item">
              <Check size={16} className="circledin-footer__feature-icon" />
              <span>No Credit Checks</span>
            </div>
            <div className="circledin-footer__feature-item">
              <Check size={16} className="circledin-footer__feature-icon" />
              <span>No Annual Commitments</span>
            </div>
            <div className="circledin-footer__feature-item">
              <Check size={16} className="circledin-footer__feature-icon" />
              <span>5-Minute Setup</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Footer Content */}
      <div className="circledin-footer__content">
        <div className="circledin-footer__columns">
          {/* Column 1: About */}
          <div className="circledin-footer__column">
            <div className="circledin-footer__brand">
              <div className="circledin-footer__brand-logo">
                <span>C</span>
              </div>
              <h2 className="circledin-footer__brand-name">Circledin</h2>
            </div>
            
            <p className="circledin-footer__about-text">
              Circledin helps you save on premium phone plans by joining or creating
              circles for group discounts. Join our community today and start saving!
            </p>
            
            <div className="circledin-footer__newsletter">
              <p className="circledin-footer__newsletter-title">Get savings tips & updates</p>
              <div className="circledin-footer__form-group">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="circledin-footer__input"
                />
                <button className="circledin-footer__button">
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Column 2: Links */}
          <div className="circledin-footer__column">
            <h3 className="circledin-footer__column-title">Quick Links</h3>
            <ul className="circledin-footer__links">
              <li><a href="/how-it-works"><ChevronRight size={14} className="circledin-footer__link-icon" /> How It Works</a></li>
              <li><a href="/all-circles"><ChevronRight size={14} className="circledin-footer__link-icon" /> Available Plans</a></li>
              <li><a href="/how-it-works#faq"><ChevronRight size={14} className="circledin-footer__link-icon" /> FAQ</a></li>
              <li><a href="https://circledin.zendesk.com/hc/en-us" target="_blank" rel="noopener noreferrer"><ChevronRight size={14} className="circledin-footer__link-icon" /> Contact Us</a></li>
              <li><a href="/blog"><ChevronRight size={14} className="circledin-footer__link-icon" /> Blog</a></li>
              <li><a href="/success-stories"><ChevronRight size={14} className="circledin-footer__link-icon" /> Success Stories</a></li>
            </ul>
          </div>
          
          {/* Column 3: Services */}
          <div className="circledin-footer__column">
            <h3 className="circledin-footer__column-title">Our Services</h3>
            <ul className="circledin-footer__links">
              <li><a href="/all-circles?carrier=Verizon"><ChevronRight size={14} className="circledin-footer__link-icon" /> Verizon Plans</a></li>
              <li><a href="/all-circles?carrier=AT%2526T"><ChevronRight size={14} className="circledin-footer__link-icon" /> AT&T Plans</a></li>
              <li><a href="/all-circles?carrier=T-Mobile"><ChevronRight size={14} className="circledin-footer__link-icon" /> T-Mobile Plans</a></li>
              <li><a href="/all-circles?carrier=Google%20Fi"><ChevronRight size={14} className="circledin-footer__link-icon" /> Google Fi Plans</a></li>
              <li><a href="/all-circles?carrier=Cricket"><ChevronRight size={14} className="circledin-footer__link-icon" /> Cricket Plans</a></li>
              <li><a href="/savings-calculator"><ChevronRight size={14} className="circledin-footer__link-icon" /> Savings Calculator</a></li>
            </ul>
          </div>
          
          {/* Column 4: Contact */}
          <div className="circledin-footer__column">
            <h3 className="circledin-footer__column-title">Connect With Us</h3>
            
            <div className="circledin-footer__social">
              <h4 className="circledin-footer__social-title">Follow Us</h4>
              <div className="circledin-footer__social-icons">
                <a href="https://facebook.com/circledin" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
                <a href="https://twitter.com/circledin" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <Twitter size={20} />
                </a>
                <a href="https://instagram.com/circledin" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="https://linkedin.com/company/circledin" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="circledin-footer__bottom">
        <div className="circledin-footer__bottom-content">
          <p className="circledin-footer__copyright">&copy; 2025 Circledin. All rights reserved.</p>
          <div className="circledin-footer__legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/cookie-policy">Cookie Policy</Link>
            <Link to="/accessibility">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;